import * as THREE from 'three';

interface Program { usedTimes: number }
interface Retainer { renderer: THREE.WebGLRenderer; kept: THREE.Material[] }

const retainers = new Set<Retainer>();
const dispose = THREE.Material.prototype.dispose;
let patched = false;

/**
 * Keeps every shader program the renderer has linked until `release`. Three destroys a program when the last material using it is
 * disposed, so an effect that comes and goes (an arrow, a charge trail, a corpse) relinked its shaders each time it came back, stalling
 * that frame for up to a few hundred milliseconds. Disposing the last user of a program now keeps that one material instead, which holds
 * the program (and a custom shader's source registration, which its program key is built from). At most one material is kept per program.
 */
export function retainShaderPrograms(renderer: THREE.WebGLRenderer) {
  if (!patched) {
    patched = true;
    THREE.Material.prototype.dispose = function (this: THREE.Material) {
      for (const retainer of retainers) if (keep(retainer, this)) return;
      dispose.call(this);
    };
  }
  const retainer: Retainer = { renderer, kept: [] };
  retainers.add(retainer);
  return function release() {
    retainers.delete(retainer);
    for (const material of retainer.kept.splice(0)) dispose.call(material);
  };
}

function keep({ renderer, kept }: Retainer, material: THREE.Material) {
  const properties = renderer.properties as THREE.WebGLProperties & { has(object: object): boolean };
  if (!properties.has(material)) return false;
  const programs = (properties.get(material) as { programs?: Map<string, Program> }).programs;
  if (!programs || ![...programs.values()].some(program => program.usedTimes === 1)) return false;
  kept.push(material);
  return true;
}
